import { Icon, makeStyles } from '@material-ui/core'
import clsx from 'clsx'
import sortBy from 'lodash/sortBy'
import numeral from 'numeral'
import PropTypes from 'prop-types'
import React from 'react'

import { Chip, SidePanel, Typography } from 'src/modules/ui'
import { formatPrice } from 'src/utils'

import SustainabilityInfo from './SustainabilityInfo'

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.fadedGreen.main,
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    padding: theme.spacing(4),
    flexDirection: 'column',
    [theme.breakpoints.up('tablet')]: {
      flexDirection: 'row',
    },
  },
  allergenName: {
    textAlign: 'left',
  },
  allergenValue: {
    textAlign: 'right',
  },
  awardIcon: {
    width: 'auto',
  },
  chip: {
    backgroundColor: theme.palette.lightGreen.main,
    color: 'white',
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
    marginRight: theme.spacing(1),
  },
  chipUnavailable: {
    marginLeft: theme.spacing(1),
  },
  content: {
    flexGrow: 1,
  },
  soldAs: {
    alignItems: 'center',
    display: 'flex',
    fontFamily: 'Gotham',
    fontWeight: 500,
  },
  productInfo: {
    marginBottom: theme.spacing(2),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  productPhoto: {
    backgroundColor: theme.palette.fadedGreen.main,
    borderRadius: theme.shape.borderRadius,
    display: 'block',
    height: 220,
    objectFit: 'cover',
    margin: '0 auto',
    width: 220,
  },
  sidePanelContainer: {
    flexShrink: 0,
    marginLeft: 0,
    marginTop: theme.spacing(4),
    width: 'auto',
    [theme.breakpoints.up('tablet')]: {
      marginLeft: theme.spacing(4),
      marginTop: 0,
      width: 250,
    },
  },
}))

const Product = ({
  allergens,
  available,
  avEquivalentUnitSize,
  awards,
  code,
  description,
  dietaryInfo,
  id,
  name,
  nutritionalInfo,
  photo,
  price,
  showPrice,
  supplier,
  sustainability,
  unit,
  variablePrice,
  variablePriceUnit,
}) => {
  const classes = useStyles()

  const contains = allergens.filter(a => a.type === 'contains')
  const mayContain = allergens.filter(a => a.type === 'may_contain')
  return (
    <div className={classes.root}>
      <div className={classes.content}>
        <div className={classes.productInfo}>
          <Typography variant="h4" color="lightGreen">
            {supplier.name}
          </Typography>
          <Typography variant="h3" color="orange">
            {name}
          </Typography>
          <Typography>{description}</Typography>
        </div>

        <div className={classes.productInfo}>
          <Typography variant="h5" color="lightGreen">
            Sold As
          </Typography>
          <Typography className={classes.soldAs} variant="body2">
            <Chip
              label={unit || 'Unit'}
              size="small"
              className={classes.chip}
            />
            {showPrice && price != null && (
              <>
                {variablePrice && (
                  <>
                    ~{formatPrice(price * avEquivalentUnitSize)}{' '}
                    {`(${formatPrice(price)}/${variablePriceUnit})`}
                  </>
                )}
                {!variablePrice && <>{formatPrice(price)}</>}
              </>
            )}
            {!available && (
              <Chip
                label="Currently unavailable"
                size="small"
                className={classes.chipUnavailable}
              />
            )}
          </Typography>
        </div>

        {dietaryInfo.length > 0 && (
          <div className={classes.productInfo}>
            <Typography variant="h5" color="lightGreen">
              Dietary Info
            </Typography>
            {dietaryInfo.map(({ name, id }) => (
              <Chip
                label={name}
                key={id}
                size="small"
                className={classes.chip}
              />
            ))}
          </div>
        )}

        {allergens.length > 0 && (
          <div className={classes.productInfo}>
            <Typography variant="h5" color="lightGreen">
              Allergens
            </Typography>
            {contains.length > 0 && (
              <Typography variant="body2">
                <strong>Contains</strong>:{' '}
                {contains.map(c => c.name).join(', ')}
              </Typography>
            )}
            {mayContain.length > 0 && (
              <Typography variant="body2">
                <strong>May Contain</strong>:{' '}
                {mayContain.map(c => c.name).join(', ')}
              </Typography>
            )}
          </div>
        )}

        {nutritionalInfo.length > 0 && (
          <div className={classes.productInfo}>
            <Typography variant="h5" color="lightGreen">
              Nuritional Info
            </Typography>
            <table>
              <thead>
                <tr>
                  <th className={classes.allergenName}>Typical values</th>
                  <th className={classes.allergenValue}>per 100g</th>
                </tr>
              </thead>
              <tbody>
                {nutritionalInfo.map(({ id, name, value, units }) => (
                  <tr key={id}>
                    <td className={classes.allergenName}>{name}</td>
                    <td className={classes.allergenValue}>
                      {numeral(value).format('0.[00]')}
                      {units}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className={classes.sidePanelContainer}>
        {(photo || sustainability.length > 0 || awards.length > 0) && (
          <SidePanel>
            {photo && (
              <SidePanel.Item>
                <img src={photo} className={classes.productPhoto} alt={name} />
              </SidePanel.Item>
            )}
            {sustainability.length > 0 && (
              <SustainabilityInfo sustainability={sustainability} />
            )}
            {awards.length > 0 && (
              <SidePanel.Item
                Icon={({ className }) => (
                  <Icon
                    className={clsx(
                      className,
                      'fas fa-trophy',
                      classes.awardIcon
                    )}
                  />
                )}
                title="Awards"
              >
                {sortBy(awards, ['year', 'name']).map(
                  ({ id, name, year }, idx) => (
                    <Chip
                      label={`${name} (${year})`}
                      key={id}
                      size="small"
                      className={classes.chip}
                    />
                  )
                )}
              </SidePanel.Item>
            )}
          </SidePanel>
        )}
      </div>
    </div>
  )
}

Product.propTypes = {
  allergens: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      type: PropTypes.oneOf(['contains', 'may_contain']),
    })
  ),
  available: PropTypes.bool,
  awards: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      year: PropTypes.number,
    })
  ),
  code: PropTypes.string,
  createdAt: PropTypes.string,
  description: PropTypes.string,
  dietaryInfo: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    })
  ),
  id: PropTypes.number,
  name: PropTypes.string,
  nutritionalInfo: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      units: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  photo: PropTypes.string,
  price: PropTypes.string,
  showPrice: PropTypes.bool,
  subcategory: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    category: PropTypes.number,
  }),
  supplier: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  }),
  sustainability: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      document: PropTypes.string,
      expiry: PropTypes.string,
      logo: PropTypes.string,
      name: PropTypes.string,
    })
  ),
  unit: PropTypes.string,
}

Product.defaultProps = {
  showPrice: true,
}

export default Product
